import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaCrown } from 'react-icons/fa';
import BillingService from '../../services/api/billing';
import UserService from '../../services/api/user';
import { API_RESPONSE } from '../../settings/shared/constants';
import { showNotificationGlobal } from '../../components/common/NotificationProvider';
import PremiumFeatureLocked from '../../components/common/PremiumFeatureLocked';
import SAModalConfirm from '../../components/common/SAModalConfirm';
import { isPocTenant } from '../../utils/premiumAccess';
import moment from 'moment';
import '../../assets/css/billing.css';

const UserList = lazy(() => import('../usermanagement/UserList'));
const ClusterList = lazy(() => import('../cloud/ClusterList'));
const CloudList = lazy(() => import('../cloud/CloudList'));
const ClusterWorkloads = lazy(() => import('../cloud/ClusterWorkloads'));
const ClusterConfig = lazy(() => import('../cloud/ClusterConfig'));
const UsageBilling = lazy(() => import('../billing/UsageBilling'));
const AgentApps = lazy(() => import('../agentapps/AgentApps'));
const LlmProxySettings = lazy(() => import('./LlmProxySettings'));
const PolicySettings = lazy(() => import('./PolicySettings'));
const Metrics = lazy(() => import("../../pages/MetricsPage"));

/* ─────────────────────────────────────────────────────────────
   Design tokens
───────────────────────────────────────────────────────────── */
const T = {
  navy: '#0f172a',
  /** Aligns with app theme (`:root --activemenu` in custom.css) */
  themePrimary: 'var(--activemenu, #3d91e4)',
  accent: '#3b82f6',
  border: 'rgba(0,0,0,0.07)',
  borderMid: 'rgba(0,0,0,0.12)',
  bg: '#f8fafc',
  surface: '#ffffff',
  textPrimary: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  fontSans: "'DM Sans','Segoe UI',sans-serif",
};

/* ─────────────────────────────────────────────────────────────
   SVG Icons
───────────────────────────────────────────────────────────── */
const Ic = {
  ArrowLeft: () => (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <polyline points="9,2 4,7 9,12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  User: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 14c0-3 2.5-5 6-5s6 2 6 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  Cloud: () => (
    <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
      <path d="M14 13H5a4.5 4.5 0 1 1 .6-8.9A5.5 5.5 0 0 1 15 8.5 3.5 3.5 0 0 1 14 13z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  ),
  Cluster: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="2" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="14" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="2" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="14" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <line x1="8" y1="3.5" x2="8" y2="5.5" stroke="currentColor" strokeWidth="1.1" />
      <line x1="8" y1="10.5" x2="8" y2="12.5" stroke="currentColor" strokeWidth="1.1" />
      <line x1="3.5" y1="8" x2="5.5" y2="8" stroke="currentColor" strokeWidth="1.1" />
      <line x1="10.5" y1="8" x2="12.5" y2="8" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  ),
  Profile: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M3 13c0-2.5 2-4 5-4s5 1.5 5 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  Eye: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  EyeOff: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M2 2l12 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M6.5 6.3A2 2 0 0 0 8 10a2 2 0 0 0 1.7-.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M3.2 4.8C2 5.9 1.5 8 1.5 8s2.5 4.5 6.5 4.5c1.2 0 2.3-.3 3.2-.8M12.7 10.8C13.8 9.8 14.5 8 14.5 8s-2.5-4.5-6.5-4.5c-.6 0-1.1.1-1.6.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  Lock: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="3" y="7" width="10" height="7.5" rx="1.6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.2 7V5.2a2.8 2.8 0 0 1 5.6 0V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="8" cy="10.6" r="0.9" fill="currentColor" />
    </svg>
  ),
  CheckMini: () => (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <polyline points="1.5,6.2 4.4,9 10.5,2.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  ChevRight: () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <polyline points="3.5,2 6.5,5 3.5,8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  ChevDown: () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <polyline points="2,3.5 5,6.5 8,3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Workloads: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
      <rect x="9" y="2" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
      <rect x="2" y="9" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
      <rect x="9" y="9" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  Clusters: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="2.5" r="1.3" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="13.5" r="1.3" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="2.5" cy="8" r="1.3" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="13.5" cy="8" r="1.3" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  Config: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
      <ellipse cx="8" cy="8" rx="2.6" ry="6.5" stroke="currentColor" strokeWidth="1.2" />
      <line x1="1.5" y1="8" x2="14.5" y2="8" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  Sun: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.3" />
      <line x1="8" y1="1" x2="8" y2="3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="8" y1="13" x2="8" y2="15" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="1" y1="8" x2="3" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="13" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  Moon: () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M13.5 10A6 6 0 0 1 6 2.5a6 6 0 1 0 7.5 7.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  ),
  Check: () => (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <polyline points="1.5,6 4.5,9 10.5,2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Settings: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M2.93 2.93l1.41 1.41M11.66 11.66l1.41 1.41M2.93 13.07l1.41-1.41M11.66 4.34l1.41-1.41" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  Credit: () => (
    <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
      <rect x="1.5" y="4" width="15" height="11" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M1.5 8h15" stroke="currentColor" strokeWidth="1.3" />
      <rect x="3.5" y="11" width="4" height="2" rx="0.5" fill="currentColor" />
    </svg>
  ),
  Plug: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M6 2v5M10 2v5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M5 7h6v2a3 3 0 0 1-6 0V7z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M8 12v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  Shield: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.5L2.5 4v4.5c0 3.1 2.3 5.9 5.5 6.5 3.2-.6 5.5-3.4 5.5-6.5V4L8 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  ),
  Proxy: () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="4" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 8h6M8 6v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  Metrics: () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="2" width="3" height="12" rx="1" fill="currentColor"/>
    <rect x="7" y="6" width="3" height="8" rx="1" fill="currentColor"/>
    <rect x="12" y="4" width="2" height="10" rx="1" fill="currentColor"/>
  </svg>
),
};

/** Clear premium badge for settings nav — larger crown + Pro label */
function PremiumNavBadge({ active = false }) {
  return (
    <span
      title="Premium feature — upgrade required"
      aria-label="Premium"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 22,
        padding: '0 8px 0 6px',
        marginLeft: 4,
        borderRadius: 999,
        flexShrink: 0,
        background: active
          ? 'rgba(255,255,255,0.2)'
          : 'linear-gradient(135deg, rgba(245,158,11,0.16) 0%, rgba(217,119,6,0.12) 100%)',
        border: active
          ? '1px solid rgba(255,255,255,0.28)'
          : '1px solid rgba(217,119,6,0.28)',
        color: active ? '#fff' : '#b45309',
        boxShadow: active ? 'none' : '0 1px 2px rgba(217,119,6,0.08)',
        lineHeight: 1,
      }}
    >
      <FaCrown
        size={12}
        color={active ? '#fde68a' : '#d97706'}
        style={{ flexShrink: 0, filter: active ? 'drop-shadow(0 0 4px rgba(253,230,138,0.45))' : 'none' }}
        aria-hidden="true"
      />
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        Pro
      </span>
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────
   Sidebar menu structure
───────────────────────────────────────────────────────────── */
const MENU_GROUPS = [
  {
    label: 'Integrations',
    items: [
      { id: 'agentApps', icon: <Ic.Plug />, label: 'Agent Apps' },
      { id: 'policies', icon: <Ic.Shield />, label: 'Policies', premium: true },
      { id: 'metrics', icon: <Ic.Metrics />, label: 'Metrics', premium: true },
      { id: 'llm-gateway', icon: <Ic.Proxy />, label: 'LLM Gateway', premium: true },
    ],
  },
  {
    label: 'Account',
    items: [
      { id: 'user-management', icon: <Ic.User />, label: 'User Management'},
      { id: 'credit-usage', icon: <Ic.Credit />, label: 'Credit Usage' },
      { id: 'profile', icon: <Ic.Profile />, label: 'Profile' }
    ],
  },
  {
    label: 'Infrastructure',
    items: [
      { id: 'cloud', icon: <Ic.Cloud />, label: 'Cloud', premium: true },
      {
        id: 'cluster-group',
        icon: <Ic.Cluster />,
        label: 'Cluster',
        premium: true,
        children: [
          { id: 'cluster', icon: <Ic.Clusters />, label: 'Clusters', hint: 'Provision & manage' },
          { id: 'cluster-config', icon: <Ic.Config />, label: 'Configuration', hint: 'Domain & TLS' },
          { id: 'cluster-workloads', icon: <Ic.Workloads />, label: 'Resources', hint: 'Workloads & deployments' },
        ],
      },
    ],
  }
];

/* ─────────────────────────────────────────────────────────────
   Profile page
───────────────────────────────────────────────────────────── */
function getPasswordStrength(password) {
  const checks = {
    length: password.length >= 8,
    letter: /[A-Za-z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const levels = [
    { label: '', color: '#e2e8f0' },
    { label: 'Weak', color: '#ef4444' },
    { label: 'Fair', color: '#f59e0b' },
    { label: 'Good', color: '#3d91e4' },
    { label: 'Strong', color: '#16a34a' },
  ];
  return { score, checks, ...levels[score] };
}

function ProfilePasswordField({
  id,
  label,
  value,
  onChange,
  visible,
  onToggleVisible,
  disabled,
  autoComplete,
  placeholder,
  error,
  hint,
  hintTone = 'muted',
}) {
  const hintColor = hintTone === 'success' ? '#16a34a' : hintTone === 'error' ? '#dc2626' : T.textMuted;
  return (
    <div className="sp-pw-field">
      <label htmlFor={id} className="sp-pw-label">{label}</label>
      <div className={`sp-pw-control${error ? ' has-error' : ''}${disabled ? ' is-disabled' : ''}`}>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          disabled={disabled}
          autoComplete={autoComplete}
          placeholder={placeholder}
        />
        <button
          type="button"
          className="sp-pw-eye"
          onClick={onToggleVisible}
          aria-label={visible ? `Hide ${label}` : `Show ${label}`}
          disabled={disabled}
        >
          {visible ? <Ic.EyeOff /> : <Ic.Eye />}
        </button>
      </div>
      {error ? <div className="sp-pw-msg is-error">{error}</div> : null}
      {!error && hint ? <div className="sp-pw-msg" style={{ color: hintColor }}>{hint}</div> : null}
    </div>
  );
}

function ProfileSection() {
  let profile = {};
  try { profile = JSON.parse(localStorage.getItem('profile')) || {}; } catch {}

  const initials = ((profile.firstName || '')[0] || '') + ((profile.lastName || '')[0] || '');
  const [editingPassword, setEditingPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [justUpdated, setJustUpdated] = useState(false);

  const strength = getPasswordStrength(newPassword);
  const passwordsMatch = Boolean(confirmPassword) && confirmPassword === newPassword;

  const resetPasswordForm = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setFormError('');
    setFieldErrors({});
  };

  const closePasswordEditor = () => {
    resetPasswordForm();
    setEditingPassword(false);
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    const nextErrors = {};

    if (!oldPassword) nextErrors.oldPassword = 'Enter your current password';
    if (!newPassword) nextErrors.newPassword = 'Enter a new password';
    if (!confirmPassword) nextErrors.confirmPassword = 'Confirm the new password';
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }
    if (oldPassword && newPassword && oldPassword === newPassword) {
      nextErrors.newPassword = 'New password must be different from the current password';
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      setFormError('');
      return;
    }

    setFieldErrors({});
    setFormError('');
    setSubmitting(true);
    try {
      const response = await UserService.updatePassword({
        oldPassword,
        newPassword,
      });
      if (response?.status === API_RESPONSE.SUCCESS) {
        showNotificationGlobal('Password updated successfully.', 'success', 'bottomRight');
        resetPasswordForm();
        setEditingPassword(false);
        setJustUpdated(true);
        setTimeout(() => setJustUpdated(false), 4000);
      } else {
        const message = response?.errorMessage || 'Failed to update password';
        setFormError(message);
        showNotificationGlobal(message, 'error', 'bottomRight');
      }
    } catch (error) {
      const message = error?.response?.data?.errorMessage || error?.message || 'Failed to update password';
      setFormError(message);
      showNotificationGlobal(message, 'error', 'bottomRight');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: T.fontSans, padding: 0 }}>
      <style>{`
        .sp-pw-field { margin-bottom: 16px; }
        .sp-pw-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: ${T.textPrimary};
          margin-bottom: 7px;
          letter-spacing: 0.01em;
        }
        .sp-pw-control {
          position: relative;
          display: flex;
          align-items: center;
          background: #f8fafc;
          border: 1px solid #e8edf3;
          border-radius: 12px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }
        .sp-pw-control:hover { border-color: #d5dee8; background: #fff; }
        .sp-pw-control:focus-within {
          background: #fff;
          border-color: var(--activemenu, #3d91e4);
          box-shadow: 0 0 0 4px color-mix(in srgb, var(--activemenu, #3d91e4) 16%, transparent);
        }
        .sp-pw-control.has-error {
          border-color: #fca5a5;
          background: #fff;
        }
        .sp-pw-control.has-error:focus-within {
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
        }
        .sp-pw-control.is-disabled { opacity: 0.65; }
        .sp-pw-control input {
          width: 100%;
          border: none;
          background: transparent;
          padding: 12px 44px 12px 14px;
          font-size: 13.5px;
          color: ${T.textPrimary};
          font-family: ${T.fontSans};
          outline: none;
        }
        .sp-pw-control input::placeholder { color: #94a3b8; }
        .sp-pw-eye {
          position: absolute;
          right: 6px;
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 8px;
          background: transparent;
          color: ${T.textMuted};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .sp-pw-eye:hover:not(:disabled) { background: #eef2f7; color: ${T.textPrimary}; }
        .sp-pw-msg { margin-top: 6px; font-size: 12px; line-height: 1.4; }
        .sp-pw-msg.is-error { color: #dc2626; }
        .sp-sec-card {
          background: ${T.surface};
          border: 0.5px solid ${T.border};
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
        }
        .sp-sec-head {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 18px 20px;
        }
        @media (max-width: 560px) {
          .sp-sec-head { flex-wrap: wrap; }
          .sp-sec-cta, .sp-sec-ghost { margin-left: auto; }
          .sp-pw-actions { flex-wrap: wrap; }
        }
        .sp-sec-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(180deg, #eef6ff 0%, #e8f0fe 100%);
          color: var(--activemenu, #3d91e4);
          flex-shrink: 0;
        }
        .sp-sec-copy { min-width: 0; flex: 1; }
        .sp-sec-title { font-size: 14px; font-weight: 650; color: ${T.textPrimary}; }
        .sp-sec-sub { font-size: 12px; color: ${T.textSecondary}; margin-top: 3px; line-height: 1.45; }
        .sp-sec-cta {
          border: 1px solid color-mix(in srgb, var(--activemenu, #3d91e4) 28%, #e2e8f0);
          background: color-mix(in srgb, var(--activemenu, #3d91e4) 8%, #ffffff);
          color: var(--activemenu, #3d91e4);
          border-radius: 10px;
          padding: 8px 14px;
          font-size: 12.5px;
          font-weight: 650;
          cursor: pointer;
          font-family: ${T.fontSans};
          white-space: nowrap;
        }
        .sp-sec-cta:hover { background: color-mix(in srgb, var(--activemenu, #3d91e4) 14%, #ffffff); }
        .sp-sec-ghost {
          border: none;
          background: transparent;
          color: ${T.textSecondary};
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 12.5px;
          font-weight: 600;
          cursor: pointer;
          font-family: ${T.fontSans};
        }
        .sp-sec-ghost:hover { background: #f1f5f9; color: ${T.textPrimary}; }
        .sp-sec-form {
          border-top: 0.5px solid ${T.border};
          padding: 20px 20px 18px;
          background: linear-gradient(180deg, #fcfdff 0%, #ffffff 40%);
          animation: spPwIn 0.18s ease;
        }
        @keyframes spPwIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sp-pw-divider {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 4px 0 16px;
          color: ${T.textMuted};
          font-size: 11px;
          font-weight: 650;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .sp-pw-divider:before, .sp-pw-divider:after {
          content: '';
          height: 1px;
          flex: 1;
          background: #eef2f6;
        }
        .sp-pw-strength { margin: -6px 0 16px; }
        .sp-pw-bars { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: 8px; }
        .sp-pw-bar {
          height: 5px;
          border-radius: 99px;
          background: #e8edf3;
        }
        .sp-pw-checks { display: flex; flex-wrap: wrap; gap: 6px; }
        .sp-pw-chip {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 550;
          background: #f8fafc;
          color: ${T.textSecondary};
          border: 1px solid #eef2f6;
        }
        .sp-pw-chip.is-on {
          background: #f0fdf4;
          color: #15803d;
          border-color: #bbf7d0;
        }
        .sp-pw-alert {
          display: flex;
          gap: 8px;
          align-items: flex-start;
          margin-bottom: 14px;
          padding: 10px 12px;
          border-radius: 10px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #b91c1c;
          font-size: 12.5px;
          line-height: 1.45;
        }
        .sp-pw-success {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 20px 16px;
          padding: 10px 12px;
          border-radius: 10px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #15803d;
          font-size: 12.5px;
          font-weight: 550;
        }
        .sp-pw-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding-top: 6px;
        }
        .sp-pw-primary {
          border: none;
          background: var(--activemenu, #3d91e4);
          color: #fff;
          border-radius: 10px;
          padding: 10px 16px;
          font-size: 13px;
          font-weight: 650;
          cursor: pointer;
          font-family: ${T.fontSans};
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
        }
        .sp-pw-primary:hover:not(:disabled) { filter: brightness(0.96); }
        .sp-pw-primary:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
      `}</style>
      {/* Top bar */}
      <div style={{ background: T.surface, borderBottom: `0.5px solid ${T.border}`, padding: '0 28px', height: 60, display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: T.themePrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          <Ic.Profile />
        </div>
        <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>Profile</span>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 24px 48px' }}>
        {/* Avatar card */}
        <div style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 16, padding: '28px 24px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 20, boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#eeedfe', color: '#534ab7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 600, flexShrink: 0 }}>
            {initials.toUpperCase() || '?'}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.textPrimary }}>{profile.firstName || ''} {profile.lastName || ''}</div>
            <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 3 }}>{profile.email || ''}</div>
            <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 100, background: '#eaf3de', color: '#3b6d11', fontSize: 11, fontWeight: 500 }}>
              {profile.role || 'Member'}
            </div>
          </div>
        </div>

        {/* Info fields */}
        <div style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)' }}>
          <div style={{ padding: '14px 24px', borderBottom: `0.5px solid ${T.border}` }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>Account information</span>
          </div>
          {[
            { label: 'First name', value: profile.firstName || '—' },
            { label: 'Last name', value: profile.lastName || '—' },
            { label: 'Email', value: profile.email || '—' },
            { label: 'Organisation', value: profile.accountName || '—' },
            { label: 'Role', value: profile.role || '—' },
          ].map(({ label, value }, i, arr) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', borderBottom: i < arr.length - 1 ? `0.5px solid ${T.border}` : 'none' }}>
              <span style={{ fontSize: 12, color: T.textSecondary, fontWeight: 500 }}>{label}</span>
              <span style={{ fontSize: 13, color: T.textPrimary }}>{value}</span>
            </div>
          ))}
        </div>

        <div className="sp-sec-card">
          <div className="sp-sec-head">
            <div className="sp-sec-icon"><Ic.Lock /></div>
            <div className="sp-sec-copy">
              <div className="sp-sec-title">Password & security</div>
              <div className="sp-sec-sub">
                {editingPassword
                  ? 'Enter your current password, then choose a new one.'
                  : 'Keep your account protected with a password only you know.'}
              </div>
            </div>
            {editingPassword ? (
              <button type="button" className="sp-sec-ghost" onClick={closePasswordEditor} disabled={submitting}>
                Cancel
              </button>
            ) : (
              <button type="button" className="sp-sec-cta" onClick={() => { setJustUpdated(false); setEditingPassword(true); }}>
                Change password
              </button>
            )}
          </div>

          {justUpdated && !editingPassword ? (
            <div className="sp-pw-success">
              <Ic.CheckMini />
              Password updated successfully.
            </div>
          ) : null}

          {editingPassword ? (
            <form className="sp-sec-form" onSubmit={handleChangePassword} noValidate>
              {formError ? <div className="sp-pw-alert">{formError}</div> : null}

              <ProfilePasswordField
                id="profile-current-password"
                label="Current password"
                value={oldPassword}
                onChange={(e) => {
                  setOldPassword(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, oldPassword: '' }));
                }}
                visible={showOldPassword}
                onToggleVisible={() => setShowOldPassword((v) => !v)}
                disabled={submitting}
                autoComplete="current-password"
                placeholder="Enter your current password"
                error={fieldErrors.oldPassword}
              />

              <div className="sp-pw-divider">New password</div>

              <ProfilePasswordField
                id="profile-new-password"
                label="New password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, newPassword: '' }));
                }}
                visible={showNewPassword}
                onToggleVisible={() => setShowNewPassword((v) => !v)}
                disabled={submitting}
                autoComplete="new-password"
                placeholder="Create a new password"
                error={fieldErrors.newPassword}
              />

              {newPassword ? (
                <div className="sp-pw-strength">
                  <div className="sp-pw-bars" aria-hidden="true">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="sp-pw-bar"
                        style={{ background: i < strength.score ? strength.color : '#e8edf3' }}
                      />
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: T.textMuted }}>Password strength</span>
                    <span style={{ fontSize: 11, fontWeight: 650, color: strength.color }}>{strength.label}</span>
                  </div>
                  <div className="sp-pw-checks">
                    {[
                      { key: 'length', label: '8+ characters' },
                      { key: 'letter', label: 'A letter' },
                      { key: 'number', label: 'A number' },
                      { key: 'special', label: 'A symbol' },
                    ].map((item) => (
                      <span key={item.key} className={`sp-pw-chip${strength.checks[item.key] ? ' is-on' : ''}`}>
                        {strength.checks[item.key] ? <Ic.CheckMini /> : null}
                        {item.label}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              <ProfilePasswordField
                id="profile-confirm-password"
                label="Confirm new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, confirmPassword: '' }));
                }}
                visible={showConfirmPassword}
                onToggleVisible={() => setShowConfirmPassword((v) => !v)}
                disabled={submitting}
                autoComplete="new-password"
                placeholder="Re-enter the new password"
                error={fieldErrors.confirmPassword}
                hint={passwordsMatch ? 'Passwords match' : ''}
                hintTone={passwordsMatch ? 'success' : 'muted'}
              />

              <div className="sp-pw-actions">
                <button type="button" className="sp-sec-ghost" onClick={closePasswordEditor} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="sp-pw-primary" disabled={submitting}>
                  {submitting ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Main Settings Page
───────────────────────────────────────────────────────────── */
const ALL_SECTION_IDS = MENU_GROUPS.flatMap((group) =>
  group.items.flatMap((item) =>
    item.children?.length ? item.children.map((child) => child.id) : [item.id]
  )
);
const PAID_DEFAULT_SECTION = 'agentApps';
const POC_DEFAULT_SECTION = 'agentApps';
const VIEW_QUERY_KEY = 'view';

function getDefaultSection() {
  return isPocTenant() ? POC_DEFAULT_SECTION : PAID_DEFAULT_SECTION;
}

function getVisibleMenuGroups() {
  return MENU_GROUPS;
}

function findParentGroupId(sectionId) {
  for (const group of MENU_GROUPS) {
    for (const item of group.items) {
      if (item.children?.some((c) => c.id === sectionId)) return item.id;
    }
  }
  return null;
}

function resolveSectionFromParams(searchParams) {
  const fromView = searchParams.get(VIEW_QUERY_KEY);
  const fromSection = searchParams.get('section'); // legacy deep links
  const candidate = fromView || fromSection;
  if (candidate && ALL_SECTION_IDS.includes(candidate)) return candidate;
  return getDefaultSection();
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionFromUrl = resolveSectionFromParams(searchParams);
  const [activeSection, setActiveSection] = useState(sectionFromUrl);
  const [fadeKey, setFadeKey] = useState(0);
  const [billingData, setBillingData] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const unsavedRef = useRef(false);
  const [pendingNav, setPendingNav] = useState(null);
  const handleUnsavedChange = useCallback((value) => { unsavedRef.current = value; }, []);
  const [expandedNav, setExpandedNav] = useState(() => {
    const parent = findParentGroupId(sectionFromUrl);
    return parent ? { [parent]: true } : {};
  });

  let profile = {};
  try { profile = JSON.parse(localStorage.getItem('profile')) || {}; } catch {}
  const initials = ((profile.firstName || '')[0] || '') + ((profile.lastName || '')[0] || '');
  const displayName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'User';
  const lastLogin = profile.lastLogin;
  const pocTenant = isPocTenant();
  const visibleMenuGroups = getVisibleMenuGroups();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Keep URL ?view= in sync on first load / legacy ?section= links
  useEffect(() => {
    const currentView = searchParams.get(VIEW_QUERY_KEY);
    if (currentView === sectionFromUrl) return;
    const next = new URLSearchParams(searchParams);
    next.set(VIEW_QUERY_KEY, sectionFromUrl);
    next.delete('section');
    setSearchParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reflect browser back/forward and external ?view= changes
  useEffect(() => {
    if (sectionFromUrl !== activeSection) {
      setActiveSection(sectionFromUrl);
      setFadeKey((prev) => prev + 1);
    }
    const parent = findParentGroupId(sectionFromUrl);
    if (parent) {
      setExpandedNav((prev) => (prev[parent] ? prev : { ...prev, [parent]: true }));
    }
  }, [sectionFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeSection !== 'credit-usage') return;
    const accountId = localStorage.getItem('accountId');
    if (!accountId) {
      setLoadingBilling(false);
      return;
    }
    setLoadingBilling(true);
    BillingService.getAccountCreditUsageSummary()
      .then((res) => {
        if (res?.status === API_RESPONSE.SUCCESS) setBillingData(res);
      })
      .finally(() => setLoadingBilling(false));
  }, [activeSection]);

  const handleBackToHome = () => navigate('/apps');

    const applySectionChange = (id) => {
    if (!ALL_SECTION_IDS.includes(id)) return;

    const next = new URLSearchParams(searchParams);
    next.set(VIEW_QUERY_KEY, id);
    next.delete('section');
    setSearchParams(next, { replace: false });

    const parent = findParentGroupId(id);
    if (parent) {
      setExpandedNav((prev) => ({ ...prev, [parent]: true }));
    }

    if (id !== activeSection) {
      setActiveSection(id);
      setFadeKey((prev) => prev + 1);
    }
  };

  const handleSectionChange = (id) => {
    if (unsavedRef.current && id !== activeSection) {
      setPendingNav(id);
      return;
    }
    applySectionChange(id);
  };

  const toggleNavGroup = (groupId, defaultChildId) => {
    const willOpen = !expandedNav[groupId];
    setExpandedNav((prev) => ({ ...prev, [groupId]: willOpen }));
    const parentOfActive = findParentGroupId(activeSection);
    if (willOpen && parentOfActive !== groupId && defaultChildId) {
      handleSectionChange(defaultChildId);
    }
  };

  const renderContent = () => {
    const premiumGate = (featureName) =>
      pocTenant ? <PremiumFeatureLocked featureName={featureName} /> : null;

    switch (activeSection) {
      // case 'user-management': return premiumGate('User Management') || <UserList />;
      case 'user-management': return <UserList />;
      case 'cloud': return premiumGate('Cloud') || <CloudList />;
      case 'cluster': return premiumGate('Cluster') || <ClusterList />;
      case 'cluster-config': return premiumGate('Cluster') || <ClusterConfig />;
      case 'cluster-workloads': return premiumGate('Cluster') || <ClusterWorkloads />;
      case 'agentApps': return <AgentApps />;
      case "metrics":return premiumGate('Metrics') ||  <Metrics />;
            case 'policies': return premiumGate('Policies') || <PolicySettings onUnsavedChange={handleUnsavedChange} />;
      case 'llm-gateway': return premiumGate('LLM Gateway') || <LlmProxySettings />;
      case 'credit-usage':
        if (loadingBilling) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 12, color: T.textMuted, fontSize: 13 }}>
              <span>Loading billing info…</span>
            </div>
          );
        }
        return <UsageBilling billingData={billingData} onClose={() => {}} />;
      case 'profile': return <ProfileSection />;
      default: return null;
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .sp-fade { animation: spFadeIn 0.18s ease; }
        @keyframes spFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .sp-back:hover { background: rgba(0,0,0,0.05) !important; }

        .sp-sidebar {
          width: 248px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          min-height: 0;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 42%);
          border-right: 1px solid rgba(0,0,0,0.06);
        }
        .sp-sidebar-top {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 18px 16px 14px;
          border-bottom: 1px solid rgba(0,0,0,0.05);
          flex-shrink: 0;
        }
        .sp-sidebar-top-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          background: var(--activemenu, #3d91e4);
          flex-shrink: 0;
          box-shadow: 0 4px 12px color-mix(in srgb, var(--activemenu, #3d91e4) 28%, transparent);
        }
        .sp-sidebar-top-title {
          font-size: 14px;
          font-weight: 600;
          color: ${T.textPrimary};
          line-height: 1.2;
        }
        .sp-sidebar-top-sub {
          font-size: 11px;
          color: ${T.textMuted};
          margin-top: 2px;
          line-height: 1.2;
        }
        .sp-sidebar-nav {
          flex: 1;
          padding: 14px 10px 20px;
          overflow-y: auto;
          min-height: 0;
        }
        .sp-nav-group { margin-bottom: 18px; }
        .sp-nav-group-label {
          font-size: 10px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: ${T.textMuted};
          padding: 0 10px;
          margin-bottom: 6px;
        }
        .sp-nav-btn {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          text-align: left;
          padding: 5px;
          border-radius: 10px;
          border: 1px solid transparent;
          margin-bottom: 2px;
          font-size: 13px;
          font-weight: 400;
          cursor: pointer;
          background: transparent;
          color: ${T.textSecondary};
          transition: background 0.15s, color 0.15s, border-color 0.15s;
        }
        .sp-nav-btn:hover:not(.active) {
          background: color-mix(in srgb, var(--activemenu, #3d91e4) 8%, #ffffff);
          color: ${T.textPrimary};
        }
        .sp-nav-btn.active {
          background: color-mix(in srgb, var(--activemenu, #3d91e4) 12%, #ffffff);
          border-color: color-mix(in srgb, var(--activemenu, #3d91e4) 22%, transparent);
          color: var(--activemenu, #3d91e4);
          font-weight: 500;
        }
        .sp-nav-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: #f1f5f9;
          color: ${T.textSecondary};
          transition: background 0.15s, color 0.15s;
        }
        .sp-nav-btn:hover:not(.active) .sp-nav-icon {
          background: color-mix(in srgb, var(--activemenu, #3d91e4) 12%, #ffffff);
          color: var(--activemenu, #3d91e4);
        }
        .sp-nav-btn.active .sp-nav-icon {
          background: color-mix(in srgb, var(--activemenu, #3d91e4) 18%, #ffffff);
          color: var(--activemenu, #3d91e4);
        }
        .sp-nav-label {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .sp-nav-chev {
          width: 20px;
          height: 20px;
          border-radius: 6px;
          color: ${T.textMuted};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: transparent;
          transition: transform 0.2s ease, background 0.15s, color 0.15s;
        }
        .sp-nav-btn:hover .sp-nav-chev,
        .sp-nav-btn.active .sp-nav-chev {
          background: color-mix(in srgb, var(--activemenu, #3d91e4) 10%, #ffffff);
          color: var(--activemenu, #3d91e4);
        }
        .sp-nav-chev.is-open {
          transform: rotate(180deg);
        }

        .sp-nav-branch {
          margin-bottom: 4px;
        }
        .sp-nav-branch.is-open {
          background: color-mix(in srgb, var(--activemenu, #3d91e4) 5%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--activemenu, #3d91e4) 14%, transparent);
          border-radius: 12px;
          padding: 3px;
          margin-bottom: 6px;
        }
        .sp-nav-branch.is-open > .sp-nav-btn {
          margin-bottom: 0;
        }
        .sp-nav-branch.is-open > .sp-nav-btn.active,
        .sp-nav-branch.is-open > .sp-nav-btn {
          background: transparent;
          border-color: transparent;
        }
        .sp-nav-branch.is-open > .sp-nav-btn:hover:not(.active) {
          background: color-mix(in srgb, var(--activemenu, #3d91e4) 8%, #ffffff);
        }

        .sp-nav-sub {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: 2px 3px 3px;
          animation: spSubIn 0.18s ease;
        }
        @keyframes spSubIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sp-nav-sub-btn {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          text-align: left;
          padding: 8px 10px 8px 12px;
          border-radius: 9px;
          border: none;
          font-size: 12.5px;
          font-weight: 500;
          cursor: pointer;
          background: transparent;
          color: ${T.textSecondary};
          transition: background 0.15s, color 0.15s;
        }
        .sp-nav-sub-btn::before {
          content: '';
          position: absolute;
          left: 4px;
          top: 50%;
          transform: translateY(-50%);
          width: 2px;
          height: 0;
          border-radius: 2px;
          background: var(--activemenu, #3d91e4);
          transition: height 0.15s ease;
        }
        .sp-nav-sub-btn:hover:not(.active) {
          background: rgba(255,255,255,0.75);
          color: ${T.textPrimary};
        }
        .sp-nav-sub-btn.active {
          background: #fff;
          color: var(--active-color, #033b70);
          font-weight: 600;
          box-shadow: 0 1px 2px rgba(15,23,42,0.05);
        }
        .sp-nav-sub-btn.active::before {
          height: 14px;
        }
        .sp-nav-sub-icon {
          width: 24px;
          height: 24px;
          border-radius: 7px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
          color: ${T.textMuted};
          flex-shrink: 0;
        }
        .sp-nav-sub-btn.active .sp-nav-sub-icon {
          background: color-mix(in srgb, var(--activemenu, #3d91e4) 14%, #ffffff);
          color: var(--activemenu, #3d91e4);
        }
        .sp-nav-sub-copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .sp-nav-sub-label {
          line-height: 1.2;
        }
        .sp-nav-sub-hint {
          font-size: 10.5px;
          font-weight: 450;
          color: ${T.textMuted};
          line-height: 1.2;
        }
        .sp-nav-sub-btn.active .sp-nav-sub-hint {
          color: color-mix(in srgb, var(--activemenu, #3d91e4) 70%, #64748b);
        }
      `}</style>

      <div style={{ fontFamily: T.fontSans, background: T.bg, height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* ── Global topbar ── */}
        <div style={{ background: T.surface, borderBottom: `0.5px solid ${T.border}`, height: 52, display: 'flex', alignItems: 'center', padding: '0 24px', justifyContent: 'space-between', flexShrink: 0, position: 'sticky', top: 0, zIndex: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              className="sp-back"
              onClick={handleBackToHome}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: T.textSecondary, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, borderRadius: 6, padding: '4px 8px', transition: 'background 0.1s' }}
            >
              <Ic.ArrowLeft /> Back to home
            </button>
            <div style={{ width: 1, height: 18, background: T.border }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>Settings</span>
          </div>

          {/* Profile chip */}
          <div
            onClick={() => handleSectionChange('profile')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 10px', borderRadius: 8, border: `0.5px solid ${T.borderMid}`, transition: 'background 0.1s', background: activeSection === 'profile' ? 'color-mix(in srgb, var(--activemenu, #3d91e4) 12%, #ffffff)' : 'transparent' }}
          >
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#eeedfe', color: '#534ab7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
              {initials.toUpperCase() || '?'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
  <span style={{
    fontSize: 12,
    fontWeight: 500,
    color: T.textSecondary,
    maxWidth: 120,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }}>
    {displayName}
  </span>

  <span style={{ fontSize: 10, color: T.textMuted || '#999' }}>
    {lastLogin
      ? moment.utc(lastLogin).local().format('MMM DD, YYYY · hh:mm A')
      : 'Not logged in yet'}
  </span>
</div>
          </div>
        </div>

        {/* ── Two-column layout ── */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* ── Left sidebar ── */}
          <aside className="sp-sidebar">

            <nav className="sp-sidebar-nav">
              {visibleMenuGroups.map((group) => (
                <div key={group.label} className="sp-nav-group">
                  <div className="sp-nav-group-label">{group.label}</div>
                  {group.items.map((item) => {
                    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                    const childActive = hasChildren && item.children.some((c) => c.id === activeSection);
                    const isExpanded = hasChildren && !!expandedNav[item.id];
                    const isActive = !hasChildren && activeSection === item.id;
                    const defaultChildId = hasChildren ? item.children[0].id : null;

                    if (hasChildren) {
                      return (
                        <div key={item.id} className={`sp-nav-branch${isExpanded ? ' is-open' : ''}`}>
                          <button
                            type="button"
                            className={`sp-nav-btn${childActive ? ' active' : ''}`}
                            onClick={() => toggleNavGroup(item.id, defaultChildId)}
                            aria-expanded={!!isExpanded}
                          >
                            <span className="sp-nav-icon">{item.icon}</span>
                            <span className="sp-nav-label">{item.label}</span>
                            {pocTenant && item.premium ? (
                              <PremiumNavBadge active={false} />
                            ) : null}
                            <span className={`sp-nav-chev${isExpanded ? ' is-open' : ''}`}>
                              <Ic.ChevDown />
                            </span>
                          </button>
                          {isExpanded ? (
                            <div className="sp-nav-sub" role="group" aria-label={`${item.label} sections`}>
                              {item.children.map((child) => {
                                const childIsActive = activeSection === child.id;
                                return (
                                  <button
                                    key={child.id}
                                    type="button"
                                    className={`sp-nav-sub-btn${childIsActive ? ' active' : ''}`}
                                    onClick={() => handleSectionChange(child.id)}
                                  >
                                    <span className="sp-nav-sub-icon">{child.icon}</span>
                                    <span className="sp-nav-sub-copy">
                                      <span className="sp-nav-sub-label">{child.label}</span>
                                      {child.hint ? (
                                        <span className="sp-nav-sub-hint">{child.hint}</span>
                                      ) : null}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    }

                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`sp-nav-btn${isActive ? ' active' : ''}`}
                        onClick={() => handleSectionChange(item.id)}
                      >
                        <span className="sp-nav-icon">{item.icon}</span>
                        <span className="sp-nav-label">{item.label}</span>
                        {pocTenant && item.premium ? (
                          <PremiumNavBadge active={false} />
                        ) : null}
                        {isActive ? (
                          <span className="sp-nav-chev"><Ic.ChevRight /></span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>
          </aside>

          {/* ── Right content ── */}
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', minHeight: 0 }}>
            <div key={fadeKey} className="sp-fade">
              <Suspense
                fallback={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: T.textMuted, fontSize: 13, fontFamily: T.fontSans }}>
                    Loading…
                  </div>
                }
              >
                {renderContent()}
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      <SAModalConfirm
        show={pendingNav !== null}
        centered
        title="Leave without creating the group?"
        message="You have selected policies that haven't been added to a group. If you leave now, your selection will be lost."
        okText="Leave"
        cancelText="Stay"
        handleOk={() => {
          const id = pendingNav;
          setPendingNav(null);
          unsavedRef.current = false;
          applySectionChange(id);
        }}
        handleCancel={() => setPendingNav(null)}
      />
    </>
  );
};

export default SettingsPage;